import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/inspection-checklists"
)({
  component: RouteComponent,
});

function RouteComponent() {
  // InspectionChecklistViewSet's read_permission ("inventory.stock.view")
  // gates list/retrieve too, with no degrade-to-own-rows fallback, so the
  // whole page is protected up front; write_permission
  // ("inventory.item.manage") gates just the write UI within.
  return (
    <ProtectedRoute requiredPermission="inventory.stock.view">
      <InspectionChecklistsPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchInspectionChecklists,
  useInspectionChecklistMutations,
} from "@/hooks/api/inventory/useCollectionsLabels";
import { InspectionChecklistTable } from "@/components/inventory/tables/InspectionChecklistTable";
import { InspectionChecklistFormSheet } from "@/components/inventory/forms/InspectionChecklistFormSheet";
import type {
  InspectionChecklist,
  InspectionChecklistPayload,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function InspectionChecklistsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.item.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const checklistsQuery = useFetchInspectionChecklists({
    page_size: pageSize,
    page,
  });
  const checklists = checklistsQuery.data?.results ?? [];
  const count = checklistsQuery.data?.count ?? 0;

  const { createChecklist, updateChecklist, deleteChecklist } =
    useInspectionChecklistMutations();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<InspectionChecklist | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InspectionChecklist | null>(
    null
  );

  const isMutating =
    createChecklist.isPending ||
    updateChecklist.isPending ||
    deleteChecklist.isPending;
  const isLoading = checklistsQuery.isPending || isMutating;
  const isFetching = checklistsQuery.isFetching || isMutating;

  const handleSubmit = async (data: InspectionChecklistPayload) => {
    if (editing) {
      await updateChecklist.mutateAsync({ id: editing.id, payload: data });
      toast.success("Checklist updated");
    } else {
      await createChecklist.mutateAsync(data);
      toast.success("Checklist created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteChecklist.mutateAsync({ id: deleteTarget.id });
      toast.success("Checklist deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Inspection Checklists"
          description="Define reusable checklist templates for item inspections"
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setIsViewOnly(false);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="New Checklist"
          isLoading={isFetching}
        />

        <InspectionChecklistTable
          checklists={checklists}
          isLoading={isLoading}
          onView={(c) => {
            setEditing(c);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(c) => {
            setEditing(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => setDeleteTarget(c)}
          onNameClick={(c) =>
            navigate({
              to: "/admin/inventory/inspection-checklists/$id",
              params: { id: String(c.id) },
            })
          }
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <InspectionChecklistFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Checklist"
          description={`Delete checklist "${deleteTarget?.name}"?`}
          itemName={deleteTarget?.name}
          isLoading={deleteChecklist.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
